/** @typedef {import("../../api/types").ClientsPage} ClientsPage */
import React, {useEffect, useState} from "react";
import axios from "axios";
import ClickableRow from "../../shared/components/ClickableRow";
import EmailLink from "../../shared/components/EmailLink";
import PhoneLink from "../../shared/components/PhoneLink";
import SearchBox from "../../shared/components/SearchBox";
import SortableTh from "../../shared/components/SortableTh";
import {useDebouncedSearch} from "../../hooks/useDebouncedSearch";
import {useListParams} from "../../hooks/useListParams";
import {useScrollRestore} from "../../hooks/useScrollRestore";
import {Link} from "react-router-dom";
import {Alert, Button, Container, Spinner, Table} from "reactstrap";
import IconLabel from "../../shared/components/IconLabel";
import {ChevronLeftIcon, ChevronRightIcon, PlusIcon} from "../../shared/components/icons";

const CLIENTS_URL = "/os/api/clients/";
const DEFAULT_ORDERING = "name"; // by name, A to Z

const Clients = () => {
    // The list's state lives in the address, so it survives a visit to a client.
    // `ordering` is the API's value: a field, "-" in front for descending.
    const {page, search, ordering, setPage, setSearch, setOrdering} = useListParams({
        basePath: "/clients",
        defaultOrdering: DEFAULT_ORDERING,
    });
    // The request waits for a pause in typing; a new search starts from page 1.
    const {text: searchText, setText: setSearchText} = useDebouncedSearch(search, setSearch);
    const [data, setData] = useState(/** @type {ClientsPage|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        axios
            .get(CLIENTS_URL, {params: {page, search: search || undefined, ordering}, signal: controller.signal})
            .then(({data}) => setData(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError("Não foi possível carregar os clientes.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [page, search, ordering]);

    useScrollRestore(!loading);

    const emptyMessage = search ? `Nenhum cliente encontrado para “${search}”.` : "Nenhum cliente encontrado.";

    const sort = {ordering, onChange: setOrdering};

    const clients = data?.results ?? [];
    const pagination = data?.pagination;

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <SearchBox
                    value={searchText}
                    onChange={setSearchText}
                    placeholder="Buscar por nome ou e-mail"
                    ariaLabel="Buscar clientes"
                />
                <Button color="primary" tag={Link} to="/clients/new">
                    <IconLabel icon={<PlusIcon />}>Criar</IconLabel>
                </Button>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            <Table hover responsive className="table-cards">
                <thead>
                    <tr>
                        <SortableTh field="name" sort={sort}>
                            Nome
                        </SortableTh>
                        <th>Telefone</th>
                        <SortableTh field="email" sort={sort}>
                            E-mail
                        </SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr>
                            <td colSpan={3} className="text-center">
                                <Spinner size="sm" />
                            </td>
                        </tr>
                    )}
                    {!loading && !error && clients.length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-center text-muted">
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                    {!loading &&
                        clients.map((client) => (
                            <ClickableRow key={client.id} to={`/clients/${client.id}`}>
                                <td>
                                    <Link to={`/clients/${client.id}`}>{client.name}</Link>
                                </td>
                                <td>
                                    <PhoneLink phone={client.phone} />
                                </td>
                                <td>
                                    <EmailLink email={client.email} />
                                </td>
                            </ClickableRow>
                        ))}
                </tbody>
            </Table>
            {pagination && pagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.prev_page}
                        onClick={() => setPage(pagination.prev_page)}
                    >
                        <IconLabel icon={<ChevronLeftIcon />}>Anterior</IconLabel>
                    </Button>
                    <span>
                        Página {pagination.curr_page} de {pagination.total_pages}
                    </span>
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.next_page}
                        onClick={() => setPage(pagination.next_page)}
                    >
                        <IconLabel icon={<ChevronRightIcon />}>Próxima</IconLabel>
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default Clients;
